import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { processDateString } from '@/javascript/lib/validate_date/process-date-string'

import { STATUS_CODE } from '../helpers/_constants'
import {
	getDefaultCourse,
	getDefaultHistoricalCourse,
	getDefaultWaiveClass,
	getDefaultWaiveData,
} from '../helpers/_constants'
import api from '../services/api'
import {
	getCertifications,
	getCourses,
	postEditCourse,
	putWaiveCourse,
} from '../services/courses'
import type {
	ActionableCourse,
	Class,
	ComplianceType,
	ComplianceTypes,
} from '../services/courses/interfaces'
import type { CourseStatusActions, HistoricalCourse } from '../services/goals/interfaces'
import type {
	Certification,
	CourseAction,
	CourseData,
	MemberTrainingCourse,
	TrainingMode,
	TrainingTab,
	WaiveData,
	WaiveTypes,
} from '../services/memberTraining/interfaces'
import type { SharedCompletion } from '../services/trainingCurriculums/interfaces'
import { getAgencyId } from '../utils/getAgencyId'
import { useAdministratorStore } from './AdministratorStore'
import { useCatalogStore } from './CatalogStore'
import GlobalStore from './GlobalStore'
import { useMembersStore } from './MembersStore'
import { useTrainingCurriculumStore } from './TrainingCurriculumStore'

export type CourseKey = 'selectedCertificationIds' | 'selectedCourseIds'

export const useMemberTrainingStore = defineStore('memberTraining', () => {
	// State
	const action = ref<CourseAction>('')
	const activeCourse = ref<Certification | Class | MemberTrainingCourse | null>(null)
	const activeTab = ref<TrainingTab>('class library')
	const canInputBundleName = ref(false)
	const canInputDueDate = ref(false)
	const certificationBeingRenamed = ref<Certification | null>(null)
	const certifications = ref<Certification[]>([])
	const combinedCourses = ref<MemberTrainingCourse[]>([])
	const complianceTypes = ref<ComplianceTypes>({
		annual: '',
		initial: '',
		none: '',
	})
	const courseStatusId = ref<string | null>(null)
	const courses = ref<MemberTrainingCourse[]>([])
	const currentCourseData = ref<CourseData>({
		description: '',
		length_in_minutes: undefined,
		name: '',
		training: { organization: '' },
	})
	const currentStep = ref(0)
	const currentlyWaiving = ref<ComplianceType>('offline_agency')
	const editCourse = ref<MemberTrainingCourse>(getDefaultCourse())
	const historicalCourse = ref<HistoricalCourse>(getDefaultHistoricalCourse())
	const isAdditional = ref(false)
	const isBulkEnrolling = ref(false)
	const isCertifications = ref(false)
	const isCertificationsEnabled = ref(false)
	const isCoursesLoading = ref(false)
	const isEditingExternalCourse = ref(false)
	const isProcessing = ref(false)
	const memberIds = ref<number[]>([])
	const membersStore = useMembersStore()
	const mode = ref<TrainingMode>('')
	const onEnrollmentConfirmation = ref(false)
	const searchInput = ref('')
	const selectedCertificationIds = ref<number[]>([])
	const selectedCourseIds = ref<number[]>([])
	const selectedSharedCompletionIds = ref<number[]>([])
	const selectedWaiveCourseId = ref<number | null>(null)
	const trainingCurriculumStore = useTrainingCurriculumStore()
	const validCourseForm = ref(false)
	const validTrainingCourseForm = ref(false)
	const waiveClass = ref<ActionableCourse>(getDefaultWaiveClass())
	const waiveData = ref<WaiveData>(getDefaultWaiveData())
	const waiveTypes = ref<WaiveTypes>({
		agency: 'offline_agency',
		careacademy: 'offline_careacademy',
		shared_completion: 'shared_completion',
	})

	// Getters
	const hasSelectedCourses = computed(() => {
		return (
			selectedCourseIds.value.length > 0 ||
			selectedCertificationIds.value.length > 0 ||
			selectedSharedCompletionIds.value.length > 0
		)
	})

	const isWaivingCourse = computed(() => {
		return [
			'add_historical',
			'edit_waived_class',
			'waive_class',
			'waive_external_class',
		].includes(action.value)
	})

	const isWaivingSharedCompletion = computed(() => {
		return currentlyWaiving.value === 'shared_completion'
	})

	const selectedCertifications = computed((): Certification[] => {
		return (
			certifications.value?.filter((course: Certification) =>
				selectedCertificationIds.value.includes(course.id)
			) || []
		)
	})

	const selectedCourses = computed((): MemberTrainingCourse[] => {
		const coursesFromLibrary = courses.value.filter(
			(course: Certification | MemberTrainingCourse) => {
				return course.id ? selectedCourseIds.value.includes(course.id) : false
			}
		)

		return [
			...combinedCourses.value,
			...coursesFromLibrary,
		]
	})

	const selectedSharedCompletions = computed((): SharedCompletion[] => {
		return trainingCurriculumStore.sharedCompletionCourses.filter(
			(course: SharedCompletion) => {
				return course.waived_completion_course_status_id
					? selectedSharedCompletionIds.value.includes(
						course.waived_completion_course_status_id
					)
					: false
			}
		)
	})

	const trainingModalHeading = computed(() => {
		switch (action.value) {
			case 'add_historical':
				return 'Add completed class'

			case 'edit_historical':
				return 'Edit completed class'

			case 'edit_waived_class':
				return 'Edit waived class'

			case 'enroll':
				return 'Add training'

			case 'waive_class':
				return 'Waive class'

			case 'waive_external_class':
				return 'Waive external class'

			default:
				return useAdministratorStore().modalHeading
		}
	})

	// Actions
	const clearModal = () => {
		GlobalStore.resetCAModal()
		resetEditCourse()
		goToStep(0)
		deselectAllCourses()
		setActiveTab('class library')

		combinedCourses.value = []
		onEnrollmentConfirmation.value = false
	}

	const courseAlreadySelected = (course: MemberTrainingCourse) => {
		return combinedCourses.value.some(c => c.id === course.id)
	}

	const createNewCourse = () => {
		const newCourse = {
			...editCourse.value,
			courseStatusActions: {
				renaming: false,
				selected: false,
				type: 'courses',
			} as CourseStatusActions,
			isModifiable: true,
			is_external: true,
		}

		combinedCourses.value.push(newCourse)
		editCourse.value = newCourse
		onEnrollmentConfirmation.value = true
	}

	const deselectAllCourses = () => {
		certifications.value?.forEach(
			certification => certification.courseStatusActions.selected = false
		)
		courses.value?.forEach(course => course.courseStatusActions.selected = false)
		selectedCertificationIds.value = []
		selectedCourseIds.value = []
		searchInput.value = ''
	}

	const deselectSingleCourse = (
		{ courseId }: { courseId?: number },
		key: CourseKey
	) => {
		if (!courseId) {
			return
		}

		const stateKey =
			key === 'selectedCourseIds'
				? selectedCourseIds
				: selectedCertificationIds

		const index = stateKey.value.indexOf(courseId)

		if (index >= 0) {
			isCourseSelected({ courseId }, key)
			stateKey.value.splice(index, 1)
		}

		let curriculum

		if (key === 'selectedCourseIds') {
			curriculum = selectedCourseIds.value
		} else {
			curriculum = isWaivingSharedCompletion.value
				? selectedSharedCompletionIds.value
				: selectedCertificationIds.value
		}

		const selectedIndex = curriculum.findIndex(id => id === courseId)

		if (selectedIndex >= 0) {
			curriculum.splice(selectedIndex, 1)
		}
	}

	const goToStep = (step: number) => {
		currentStep.value = step < 0 ? 0 : step
	}

	const isCourseSelected = (
		{ courseId }: { courseId: number },
		key: CourseKey
	) => {
		if (key === 'selectedCourseIds') {
			const course = courses.value.find(course => course.id === courseId)

			return course && course.courseStatusActions
				? course.courseStatusActions.selected =
				!course.courseStatusActions.selected
				: false
		} else {
			const course = certifications.value.find(course => course.id === courseId)

			return course && course.courseStatusActions
				? course.courseStatusActions.selected =
				!course.courseStatusActions.selected
				: false
		}
	}

	const isEditingCourse = (value: boolean) => {
		isEditingExternalCourse.value = value

		if (!value && action.value === 'enroll') {
			resetEditCourse()
		}
	}

	const isSharedCompletionSelected = ({ courseId }: {
		courseId: number | null
	}) => {
		if (!courseId) {
			return false
		}

		const course = trainingCurriculumStore.sharedCompletionCourses.find(
			course => course.waived_completion_course_status_id === courseId
		)

		return course && course.courseStatusActions
			? course.courseStatusActions.selected =
			!course.courseStatusActions.selected
			: false
	}

	const loadCertifications = async () => {
		if (isCertificationsEnabled.value) {
			try {
				const response = await getCertifications(api, getAgencyId())

				certifications.value =
					response.careAcademySpecializedCertifications.map(course => ({
						...course,
						courseStatusActions: {
							renaming: false,
							selected: false,
							type: 'certifications',
						},
					}))
			} catch (error) {
				GlobalStore.onHandleErrorMessages({ error })
			}
		}
	}

	const loadCourses = async (next: (courses: MemberTrainingCourse[]) => void) => {
		try {
			const response: MemberTrainingCourse[] = await getCourses(api, {
				agencyId: getAgencyId(),
				memberId:
					memberIds.value?.length === 1 ? memberIds.value[0] : null,
			})

			next(response)
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		} finally {
			isCoursesLoading.value = false
		}
	}

	const modifiedCourseData = (
		course: MemberTrainingCourse
	): MemberTrainingCourse => {
		let courseTrainingCompletionDate: string | undefined

		if (course?.training?.completionDate) {
			const dateValue = processDateString({
				dateString: course.training?.completionDate,
				isRequired: false,
				maxDateString: '',
				minDateString: '',
			})

			courseTrainingCompletionDate = dateValue.formattedDate
		}

		return {
			...course,
			add_to_library: course.add_to_library || false,
			courseStatusActions: {
				renaming: false,
				selected: course.courseStatusActions?.selected || false,
				type: 'courses',
			},
			course_status_id: courseStatusId.value
				? parseInt(courseStatusId.value)
				: null,
			description: course.description,
			id: course.id,
			is_external: course.is_external,
			length_in_minutes: course.length_in_minutes,
			name: course.name,
			training: {
				completionDate: courseTrainingCompletionDate ?? '',
				grade: course.training?.grade,
				location: course.training?.location ?? '',
				organization:
					course.training?.organization ||
					course.training_organization ||
					GlobalStore.agency?.name ||
					'',
				trainer: course.training?.trainer || '',
			},
			training_organization:
				course.training?.organization ||
				course.training_organization ||
				GlobalStore.agency?.name ||
				'',
			type: course.type,
		}
	}

	const next = () => {
		currentStep.value += 1
	}

	const previous = () => {
		currentStep.value = Math.max(0, currentStep.value - 1)
	}

	const removeCourseFromEnrollment = ({
		courseId,
		key,
		name,
	}: {
		courseId: number | null
		key: CourseKey
		name: string
	}) => {
		if (courseId) {
			deselectSingleCourse({ courseId }, key)
		}

		if (activeTab.value !== 'new class') {
			removeFromCreatedCourses(name)
		}
		resetEditCourse()

		if (!hasSelectedCourses.value) {
			goToStep(0)
			currentStep.value = 0
		}
	}

	const removeFromCreatedCourses = (name: string) => {
		const courseIndex = combinedCourses.value.findIndex(
			course => course.name === name
		)

		if (courseIndex >= 0) {
			combinedCourses.value.splice(courseIndex, 1)
		}
	}

	const resetCertificationBeingRenamed = (course: Certification) => {
		const certification = certifications.value.find(x => x.id === course.id)

		if (certification) {
			certification.courseStatusActions.renaming = false
			certificationBeingRenamed.value = null
		}
	}

	const resetData = () => {
		clearModal()
		unloadGoal()
		action.value = ''
		courseStatusId.value = null
		currentCourseData.value = {
			description: '',
			length_in_minutes: undefined,
			name: '',
			training: { organization: '' },
		}

		editCourse.value = getDefaultCourse()
		historicalCourse.value = getDefaultHistoricalCourse()
		isBulkEnrolling.value = false
		mode.value = ''
		isEditingExternalCourse.value = false
		waiveClass.value = getDefaultWaiveClass()
		waiveData.value = getDefaultWaiveData()
		currentlyWaiving.value = 'offline_agency'
		selectedSharedCompletionIds.value = []

		GlobalStore.clearSelectedValues()
		GlobalStore.resetCAModal()
	}

	const resetEditCourse = () => {
		editCourse.value = getDefaultCourse()
		isEditingExternalCourse.value = false
	}

	const restoreCourseData = () => {
		editCourse.value.description = currentCourseData.value.description
		editCourse.value.length_in_minutes =
			currentCourseData.value.length_in_minutes ?? 0
		editCourse.value.name = currentCourseData.value.name
		editCourse.value.training.organization =
			currentCourseData.value.training.organization
	}

	const saveCurrentCourseData = () => {
		currentCourseData.value.description = editCourse.value.description
		currentCourseData.value.length_in_minutes = editCourse.value.length_in_minutes
		currentCourseData.value.name = editCourse.value.name
		currentCourseData.value.training.organization =
			editCourse.value.training.organization
	}

	const selectMultipleCourses = (
		{ courseId }: { courseId: number },
		key: CourseKey
	) => {
		const stateKey =
			key === 'selectedCourseIds'
				? selectedCourseIds
				: selectedCertificationIds

		stateKey.value.push(courseId)
		isCourseSelected({ courseId }, key)
	}

	const selectSingleCourse = (
		{
			courseId,
			type,
		}: { courseId: number; type?: 'checkbox' | 'radio' },
		key: CourseKey = 'selectedCourseIds'
	) => {
		const stateKey =
			key === 'selectedCourseIds'
				? selectedCourseIds
				: selectedCertificationIds

		stateKey.value = [courseId]
		selectedWaiveCourseId.value = courseId

		if (type === 'radio') {
			courses.value.map(course => {
				if (course.id !== courseId) {
					course.courseStatusActions.selected = false
				}
			})
		}

		isCourseSelected({ courseId }, key)

		const course = courses.value.find(course => course.id === courseId)

		editCourse.value =
			course && 'description' in course ? course : getDefaultCourse()
	}

	const setActiveTab = (tabName: TrainingTab) => {
		activeTab.value = tabName
		isCertifications.value = tabName === 'class list'

		if (tabName === 'class list') {
			useCatalogStore().itemType = 'specialized'
		} else {
			useCatalogStore().itemType = 'course'
		}

		resetEditCourse()
	}

	const setCertificationBeingRenamed = (course: Certification) => {
		const certification = certifications.value.find(x => x.id === course.id)

		if (certification && 'classes' in certification) {
			certification.courseStatusActions.renaming = true
			certificationBeingRenamed.value = certification
		}
	}

	const setCourses = (newCourses: MemberTrainingCourse[]) => {
		if (!newCourses.length) {
			return
		}

		switch (action.value) {
			case 'add_historical':
			case 'waive_class':
			case 'waive_external_class':
				courses.value = newCourses
					.filter(course => 'is_external' in course && course.is_external)
					.map(course => modifiedCourseData(course))
				break

			default:
				courses.value = newCourses.map(course => modifiedCourseData(course))
				break
		}
	}

	const showAddTrainingModal = async ({
		action: newAction,
		canInputBundleName: newCanInputBundleName,
		canInputDueDate: newCanInputDueDate,
		isAdditional: newIsAdditional,
		isBulkEnrolling: newIsBulkEnrolling,
		isCoursesLoading: newIsCoursesLoading,
		memberIds: newMemberIds,
	}: {
		action: CourseAction
		canInputBundleName: boolean
		canInputDueDate: boolean
		isAdditional: boolean
		isBulkEnrolling: boolean
		isCoursesLoading: boolean
		memberIds: number[]
	}) => {
		clearModal()

		if (action.value.startsWith('edit')) {
			goToStep(1)
		}

		action.value = newAction
		canInputBundleName.value = newCanInputBundleName
		canInputDueDate.value = newCanInputDueDate
		isAdditional.value = newIsAdditional
		isBulkEnrolling.value = newIsBulkEnrolling
		isCoursesLoading.value = newIsCoursesLoading
		memberIds.value = newMemberIds

		GlobalStore.emittedEvent = 'add-training'

		try {
			await loadCertifications()
			await loadCourses(fetchedCourses => setCourses(fetchedCourses))
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		} finally {
			isCoursesLoading.value = false
		}
	}

	const unloadGoal = () => {
		membersStore.goal = null
		memberIds.value = []
		certifications.value = []
		courses.value = []
	}

	const updateCourse = async () => {
		if (!editCourse.value || !editCourse.value.id) {
			return
		}

		const courseData: CourseData = {
			course_id: editCourse.value.id,
			description:
				'description' in editCourse.value ? editCourse.value.description : '',
			length_in_minutes:
				'length_in_minutes' in editCourse.value
					? editCourse.value.length_in_minutes
					: undefined,
			name: editCourse.value.name,
			training: {
				organization:
					'training' in editCourse.value &&
						editCourse.value.training_organization
						? editCourse.value.training_organization
						: GlobalStore.agency?.name ?? '',
			},
		}

		try {
			await postEditCourse(api, courseData)

			const course = courses.value.find(
				course => course.id === courseData.course_id
			)

			if (course) {
				if ('description' in course) {
					course.description = courseData.description
					course.length_in_minutes = courseData.length_in_minutes ?? 0
				}

				course.name = courseData.name
			}

			if (currentStep.value === 0) {
				goToStep(0)
			}

			isEditingCourse(false)
			clearModal()

			GlobalStore.onHandleSuccessMessages({ message: `${courseData.name} has been updated.` })
		} catch (error: any) {
			if (error.request?.status === STATUS_CODE.INTERNAL_SERVER_ERROR) {
				GlobalStore.onHandleErrorMessages({
					error:
						`${courseData.name} could not be updated. There was a problem with your request.`,
				})
			} else {
				GlobalStore.onHandleErrorMessages({ error })
			}
		}
	}

	const waiveCourse = async ({
		courseType,
		memberId,
		waivedCompletionCourseStatusId,
	}: {
		courseType: ComplianceType
		memberId?: number | undefined
		waivedCompletionCourseStatusId: number | null
	}) => {
		isProcessing.value = true

		const waiveCourseData: ActionableCourse = {
			add_to_library: editCourse.value.add_to_library,
			agency_id: getAgencyId(),
			caregiver_ids: JSON.stringify(memberIds.value),
			course: {
				description: editCourse.value.description,
				finalGrade: editCourse.value.training.grade ?? undefined,
				name: editCourse.value.name,
				training: {
					completedDate: editCourse.value.training.completionDate ?? '',
					date: editCourse.value.training.completionDate ?? '',
					grade: editCourse.value.training.grade ?? undefined,
					location: editCourse.value.training.location ?? '',
					trainer: editCourse.value.training.trainer ?? '',
					waived_completion_course_status_id: waivedCompletionCourseStatusId,
				},

				type: courseType,
			},

			course_status_id: courseStatusId.value ?? null,
		}

		if (editCourse.value.add_to_library) {
			waiveCourseData['new_course'] = {
				description: editCourse.value.description,
				duration: Number(editCourse.value.length_in_minutes),
				name: editCourse.value.name,
			}

			waiveCourseData['training'] = { organization: editCourse.value.training.organization }
		}

		if (
			(action.value === 'waive_class' ||
				action.value === 'waive_external_class') &&
			courseType !== waiveTypes.value.careacademy &&
			!editCourse.value.add_to_library
		) {
			waiveCourseData['offline-courses-list'] = [
				Number(editCourse.value.id),
				editCourse.value.name,
				editCourse.value.description,
				Number(editCourse.value.length_in_minutes),
			]
		}

		try {
			await putWaiveCourse(api, getAgencyId(), waiveCourseData)
			await useAdministratorStore().retrieveMembers()

			if (memberIds.value[0] === memberId) {
				await membersStore.fetchMemberDetails(memberId)
			}

			GlobalStore.onHandleSuccessMessages({ message: `${waiveData.value.courseName} has been waived.` })

			resetData()
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		} finally {
			isProcessing.value = false
			resetData()
		}
	}

	return {
		// State
		action,
		activeCourse,
		activeTab,
		canInputBundleName,
		canInputDueDate,
		certificationBeingRenamed,
		certifications,
		combinedCourses,
		complianceTypes,
		courseStatusId,
		courses,
		currentCourseData,
		currentStep,
		currentlyWaiving,
		editCourse,
		historicalCourse,
		isAdditional,
		isBulkEnrolling,
		isCertifications,
		isCertificationsEnabled,
		isCoursesLoading,
		isEditingExternalCourse,
		isProcessing,
		memberIds,
		mode,
		onEnrollmentConfirmation,
		searchInput,
		selectedCertificationIds,
		selectedCourseIds,
		selectedSharedCompletionIds,
		selectedWaiveCourseId,
		validCourseForm,
		validTrainingCourseForm,
		waiveClass,
		waiveData,
		waiveTypes,

		// Getters
		hasSelectedCourses,
		isWaivingCourse,
		isWaivingSharedCompletion,
		selectedCertifications,
		selectedCourses,
		selectedSharedCompletions,
		trainingModalHeading,

		// Actions
		clearModal,
		courseAlreadySelected,
		createNewCourse,
		deselectAllCourses,
		deselectSingleCourse,
		goToStep,
		isCourseSelected,
		isEditingCourse,
		isSharedCompletionSelected,
		loadCertifications,
		loadCourses,
		modifiedCourseData,
		next,
		previous,
		removeCourseFromEnrollment,
		removeFromCreatedCourses,
		resetCertificationBeingRenamed,
		resetData,
		resetEditCourse,
		restoreCourseData,
		saveCurrentCourseData,
		selectMultipleCourses,
		selectSingleCourse,
		setActiveTab,
		setCertificationBeingRenamed,
		setCourses,
		showAddTrainingModal,
		unloadGoal,
		updateCourse,
		waiveCourse,
	}
})
